import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Shield, CheckCircle2 } from "lucide-react";
import { PageHero } from "./Services";
import { SITE } from "../lib/site";
import { Reveal } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Insurance() {
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    vehicle_year: "",
    vehicle_make: "",
    vehicle_model: "",
    coverage: "Standard",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.email) {
      toast.error("Please fill name, phone and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, form);
      toast.success("Quote request received. We'll call you shortly.");
      setDone(true);
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Auto Insurance"
        title={
          <>
            Bind coverage <span className="italic text-[#C5A059]">today</span>.
            <br /> Drive home <span className="italic">insured</span>.
          </>
        }
        image={SITE.images.insurance}
      />

      <section className="py-24 lg:py-32 bg-[#FBF9F5]" data-testid="insurance-section">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="label-mini">How it works</div>
            <h2 className="font-serif mt-6 text-4xl md:text-5xl leading-[1.05] text-[#0A192F]">
              Same-day auto insurance, quoted across multiple carriers.
            </h2>
            <p className="mt-6 text-[#0A192F]/70 max-w-md leading-relaxed">
              We shop your rate across trusted PA insurers — you pick the coverage
              and bind on the spot. Your ID card and proof of insurance are printed
              at the counter, and we walk your title & tags through immediately.
            </p>

            <ul className="mt-10 space-y-4">
              {[
                "Liability, full coverage, SR-22",
                "Immediate ID cards",
                "Multi-vehicle & multi-driver discounts",
                "Rideshare & commercial add-ons",
              ].map((x) => (
                <li key={x} className="flex items-start gap-3 text-[#0A192F]/85">
                  <CheckCircle2 className="w-5 h-5 text-[#C5A059] shrink-0 mt-0.5" />
                  <span>{x}</span>
                </li>
              ))}
            </ul>

            <a href={SITE.phoneHref} className="btn-gold mt-10" data-testid="insurance-call">
              <Phone className="w-4 h-4" /> Talk to an Agent
            </a>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            {done ? (
              <div
                className="border border-[#0A192F]/12 bg-white p-10"
                data-testid="quote-success"
              >
                <Shield className="w-8 h-8 text-[#C5A059]" />
                <h3 className="font-serif text-3xl mt-6 text-[#0A192F]">
                  Thanks — we've got it.
                </h3>
                <p className="mt-4 text-[#0A192F]/70">
                  An agent will call {form.phone} within business hours to confirm
                  vehicle details and finalize your quote.
                </p>
                <a href={SITE.phoneHref} className="btn-gold mt-8" data-testid="quote-success-call">
                  <Phone className="w-4 h-4" /> Or call {SITE.phone}
                </a>
              </div>
            ) : (
              <Reveal>
                <form
                  onSubmit={submit}
                  className="border border-[#0A192F]/12 bg-white p-8 md:p-10"
                  data-testid="quote-form"
                >
                  <div className="label-mini">Free Quote Request</div>
                  <h3 className="font-serif text-3xl mt-3 text-[#0A192F]">
                    Get your rate in minutes.
                  </h3>

                  <div className="grid md:grid-cols-2 gap-6 mt-8">
                    <Field label="Full Name" value={form.full_name} onChange={update("full_name")} testid="q-name" />
                    <Field label="Phone" value={form.phone} onChange={update("phone")} testid="q-phone" type="tel" />
                    <Field label="Email" value={form.email} onChange={update("email")} testid="q-email" type="email" className="md:col-span-2" />
                    <Field label="Vehicle Year" value={form.vehicle_year} onChange={update("vehicle_year")} testid="q-year" />
                    <Field label="Make" value={form.vehicle_make} onChange={update("vehicle_make")} testid="q-make" />
                    <Field label="Model" value={form.vehicle_model} onChange={update("vehicle_model")} testid="q-model" />
                    <div>
                      <div className="label-mini">Coverage</div>
                      <select
                        value={form.coverage}
                        onChange={update("coverage")}
                        className="input-line"
                        data-testid="q-coverage"
                      >
                        <option>Standard</option>
                        <option>Full Coverage</option>
                        <option>SR-22</option>
                        <option>Commercial</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <div className="label-mini">Notes</div>
                      <textarea
                        value={form.notes}
                        onChange={update("notes")}
                        rows={3}
                        className="input-line resize-none"
                        placeholder="Multi-driver, prior claims, anything we should know…"
                        data-testid="q-notes"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-gold mt-10 w-full justify-center"
                    data-testid="quote-submit"
                  >
                    {loading ? "Sending…" : "Request My Quote"}
                  </button>
                  <p className="mt-4 text-xs text-[#0A192F]/50">
                    By submitting, you consent to be contacted about your insurance quote.
                  </p>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, value, onChange, testid, type = "text", className = "" }) {
  return (
    <div className={className}>
      <div className="label-mini">{label}</div>
      <input
        type={type}
        value={value}
        onChange={onChange}
        className="input-line"
        data-testid={testid}
        required={["Full Name", "Phone", "Email"].includes(label)}
      />
    </div>
  );
}
